import * as React from 'react';

import Box from '@mui/material/Box';

import { User } from '../../../graphql/API';
import { useAppSelector } from '../../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../../utils/venue-tags';
import { extractSubscriptionPlan } from '../../SubscriptionPlans/common/helper';
import { EmptyCollab } from './EmptyCollab';
import ProfileCard from './ProfileCard';
import ProfileCardSkeleton from './ProfileCardSkeleton';
import { OnChange } from './types';

type ProfileCheckBoxGroupProps = {
  data: User[];
  isLoading: boolean;
  isFetching: boolean;
  onChange: (values: string[]) => void;
  values: string[];
  handleMarketChange?: () => void;
};

/**
 * Card grid.
 *
 * This used to be a MUI `Grid` with fixed column counts (`xs 2.4 / md 3 / lg 3
 * / xl 2.4`), which jumped straight from 4 to 5 columns at 1536px and had no
 * step below 4 — so between 900px and 1200px the cards were squeezed well past
 * the point where names and handles stayed readable. `auto-fill` derives the
 * column count from the available width instead, so it degrades smoothly at
 * every size and needs no breakpoints at all.
 */
const gridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  columnGap: 3,
  rowGap: 10,
};

const ProfileCheckBoxGroup: React.FC<ProfileCheckBoxGroupProps> = ({
  data,
  isLoading,
  isFetching,
  onChange,
  values,
  handleMarketChange,
}) => {
  const venueState = useAppSelector((state) => state.venue.value);

  const { subscription } = useAppSelector((state) => state.subscription.value);

  const handleOnChanged = React.useCallback<OnChange>(
    (e, checked) => {
      const v = e.target.value;

      const vv = checked ? [...values, v] : values.filter((y) => y !== v);
      onChange(vv);
    },
    [onChange, values],
  );

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  return (
    <>
      <Box sx={gridSx}>
        {isLoading
          ? [...Array(6)].map((_, i) => (
              <Box key={`skeleton-${i}`} sx={{ minWidth: 0 }}>
                <ProfileCardSkeleton />
              </Box>
            ))
          : data.map((x) => (
              <Box key={x.id} sx={{ minWidth: 0 }}>
                <ProfileCard
                  data={x}
                  fullProfile
                  onChange={handleOnChanged}
                  checked={values.includes(x.id)}
                  profileLinkDisabled={
                    !subscriptionPlan && venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans)
                  }
                />
              </Box>
            ))}
      </Box>
      {!isFetching && data.length === 0 && <EmptyCollab handleMarketChange={handleMarketChange} />}
    </>
  );
};

export default ProfileCheckBoxGroup;
